import { useMutation } from '@tanstack/react-query';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { Id } from '@minty/shared/ids';
import { notify } from '~/lib/toast';
import { useTRPC } from '~/lib/trpc';

/**
 * What the draft is written against, named rather than assembled: the client
 * hands over identifiers and the server reads the history behind them.
 *
 * Both null is the mailbox's new message — nobody to have a history with yet.
 */
export type SuggestionSource = {
  contactId: Id<'contacts'> | null;
  answeringEmailId: Id<'emails'> | null;
};

const NO_SOURCE: SuggestionSource = {
  contactId: null,
  answeringEmailId: null,
};

/**
 * The wand: ask for a draft, get text back or nothing.
 *
 * Returns null on failure rather than throwing, so a caller replaces the
 * composer's contents only when there is something to replace them with — a
 * failed generation must not wipe what the receptionist had typed.
 *
 * `isGenerating` is the mutation's own pending state: the draft is never
 * stored, it goes straight into a field the person then edits.
 */
export function useMessageSuggestion(
  channel: 'email' | 'sms',
  source: SuggestionSource = NO_SOURCE,
) {
  const { t } = useTranslation('mailbox');
  const trpc = useTRPC();
  const suggestMessage = useMutation(
    trpc.drafts.generation.suggestMessage.mutationOptions(),
  );
  const [isGenerating, setIsGenerating] = React.useState(false);

  const hasSource =
    source.contactId !== null || source.answeringEmailId !== null;

  const generate = async (suggestion: string): Promise<string | null> => {
    /** An empty composer is a request in itself when there is a history to
     *  answer from; without one there is nothing to write about. */
    if (suggestion.trim() === '' && !hasSource) {
      notify.error(t('Write a suggestion first.'));
      return null;
    }

    setIsGenerating(true);

    try {
      const { text } = await suggestMessage.mutateAsync({
        channel,
        suggestion,
        contactId: source.contactId,
        answeringEmailId: source.answeringEmailId,
      });

      if (text === '') {
        notify.error(t('Unable to generate a response.'));
        return null;
      }

      return text;
    } catch {
      notify.error(t('Unable to generate a response.'));
      return null;
    } finally {
      setIsGenerating(false);
    }
  };

  return { generate, isGenerating };
}
